'use client';

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { demoFolders, routingEmails } from './demo-data';

const ANALYZE_DURATION = 800;
const ROUTE_DURATION = 600;
const PAUSE_BETWEEN = 500;
const LOOP_PAUSE = 3000;

type Phase = 'idle' | 'showing' | 'analyzing' | 'routing' | 'done';

interface RoutingDemoProps {
  className?: string;
}

export function RoutingDemo({ className = '' }: RoutingDemoProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { amount: 0.3 });
  const prefersReducedMotion = useReducedMotion();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('idle');
  const [routedEmails, setRoutedEmails] = useState<Map<string, string>>(new Map());
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const currentEmail = routingEmails[currentIndex];

  function addTimer(callback: () => void, delay: number) {
    const id = setTimeout(() => {
      timersRef.current.delete(id);
      callback();
    }, delay);
    timersRef.current.add(id);
    return id;
  }

  function clearAllTimers() {
    timersRef.current.forEach(clearTimeout);
    timersRef.current.clear();
  }

  useEffect(() => {
    return () => clearAllTimers();
  }, []);

  useEffect(() => {
    if (!isInView) {
      setPhase('idle');
      setRoutedEmails(new Map());
      setCurrentIndex(0);
      clearAllTimers();
      return;
    }

    // Show static final state for reduced motion users
    if (prefersReducedMotion) {
      setPhase('done');
      const allRouted = new Map<string, string>();
      for (const email of routingEmails) {
        if (email.folder) allRouted.set(email.id, email.folder);
      }
      setRoutedEmails(allRouted);
      return;
    }

    clearAllTimers();

    addTimer(() => setPhase('showing'), 300);
    addTimer(() => setPhase('analyzing'), 800);
    addTimer(() => setPhase('routing'), 800 + ANALYZE_DURATION);

    addTimer(
      () => {
        setPhase('done');
        if (currentEmail?.folder) {
          setRoutedEmails((prev) => new Map(prev).set(currentEmail.id, currentEmail.folder!));
        }
      },
      800 + ANALYZE_DURATION + ROUTE_DURATION
    );

    addTimer(
      () => {
        const nextIndex = currentIndex + 1;
        if (nextIndex < routingEmails.length) {
          setCurrentIndex(nextIndex);
          setPhase('idle');
        } else {
          addTimer(() => {
            setRoutedEmails(new Map());
            setCurrentIndex(0);
            setPhase('idle');
          }, LOOP_PAUSE);
        }
      },
      800 + ANALYZE_DURATION + ROUTE_DURATION + PAUSE_BETWEEN
    );

    return () => clearAllTimers();
  }, [isInView, currentIndex, currentEmail, prefersReducedMotion]);

  const folderCounts = new Map<string, number>();
  for (const folder of routedEmails.values()) {
    folderCounts.set(folder, (folderCounts.get(folder) ?? 0) + 1);
  }

  const noMotion = prefersReducedMotion ?? false;

  return (
    <div
      ref={ref}
      className={`flex flex-col items-start gap-6 md:flex-row ${className}`}
      role="img"
      aria-label="Demo: incoming emails are analyzed and routed to the correct folder automatically"
    >
      <div className="w-full flex-1">
        <div className="mb-2 text-xs font-medium uppercase tracking-wider text-white/30">
          Incoming
        </div>
        <AnimatePresence mode="wait">
          {phase !== 'idle' && currentEmail && (
            <motion.div
              key={currentEmail.id}
              className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-3"
              initial={noMotion ? false : { opacity: 0, x: -20 }}
              animate={{
                opacity: phase === 'routing' || phase === 'done' ? 0.4 : 1,
                x: 0,
                scale: phase === 'done' ? 0.95 : 1,
              }}
              exit={noMotion ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
              transition={
                noMotion ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 25 }
              }
            >
              <div className="text-sm font-medium text-white/90">{currentEmail.from}</div>
              <div className="mt-0.5 text-sm text-white/50">{currentEmail.subject}</div>

              {phase === 'analyzing' && (
                <motion.div
                  className="mt-2 flex items-center gap-2 text-xs text-white/40"
                  initial={{ opacity: 0 }}
                  animate={noMotion ? { opacity: 1 } : { opacity: [0.4, 1, 0.4] }}
                  transition={noMotion ? { duration: 0 } : { duration: 1, repeat: Infinity }}
                >
                  <div className="h-1.5 w-1.5 rounded-full bg-blue-400" />
                  Analyzing...
                </motion.div>
              )}

              {(phase === 'routing' || phase === 'done') && currentEmail.folder && (
                <motion.div
                  className="mt-2 text-xs text-white/60"
                  initial={noMotion ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={noMotion ? { duration: 0 } : undefined}
                >
                  &rarr; {currentEmail.folder}
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="hidden h-full items-center pt-8 md:flex" aria-hidden="true">
        {(phase === 'routing' || phase === 'done') && (
          <motion.div
            className="h-px w-8 bg-gradient-to-r from-white/20 to-white/5"
            initial={noMotion ? false : { scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={noMotion ? { duration: 0 } : { duration: 0.3 }}
            style={{ transformOrigin: 'left' }}
          />
        )}
      </div>

      <div className="w-full flex-1">
        <div className="mb-2 text-xs font-medium uppercase tracking-wider text-white/30">
          Folders
        </div>
        <div className="flex flex-col gap-1.5">
          {demoFolders.map((folder) => {
            const count = folderCounts.get(folder.name) ?? 0;
            const isTarget =
              (phase === 'routing' || phase === 'done') && currentEmail?.folder === folder.name;

            return (
              <motion.div
                key={folder.name}
                className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${folder.bgColor} ${folder.borderColor} ${
                  isTarget ? 'ring-1 ring-white/10' : ''
                }`}
                animate={{
                  scale: isTarget && !noMotion ? 1.02 : 1,
                }}
                transition={
                  noMotion ? { duration: 0 } : { type: 'spring', stiffness: 400, damping: 20 }
                }
              >
                <span className={`font-medium ${folder.color}`}>{folder.name}</span>
                {count > 0 && (
                  <motion.span
                    className="text-xs text-white/40"
                    initial={noMotion ? false : { opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={noMotion ? { duration: 0 } : undefined}
                    key={count}
                  >
                    {count}
                  </motion.span>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
